import { Skeleton } from "@/components/ui/skeleton";
import {
  useClientLinks,
  useScopedClient,
} from "@/features/clients/hooks/use-scoped-client";
import { useCustomerSummary } from "@/features/customers/hooks/use-customer-summary";
import { CustomerNameText } from "@/components/molecules/unsure-name-mark";
import { EnvelopeIcon, PhoneIcon, UserIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/features/utils/i18n";

interface CustomerTileProps {
  customerId: string;
  fallback?: string;
  clickable?: boolean;
  /**
   * `inline` puts the name and the contacts on one line behind a small avatar —
   * for a header that already has a title of its own (the call review).
   * `compact` is the name over one contact, the phone first — for a list where
   * the customer is one column of many (the calls table).
   */
  variant?: "tile" | "inline" | "compact";
}

export function CustomerTile({
  customerId,
  fallback,
  clickable = false,
  variant = "tile",
}: CustomerTileProps) {
  const { t } = useTranslation();
  fallback ??= t("common.unknown");
  const { clientId } = useScopedClient();
  const links = useClientLinks();
  const { data: customer, isLoading } = useCustomerSummary(
    clientId,
    customerId,
  );

  if (!customerId) {
    return <span className="text-muted-foreground">{fallback}</span>;
  }

  if (isLoading) {
    if (variant === "inline") return <Skeleton className="h-5 w-64" />;
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    );
  }

  // Get primary contact info from structured addresses
  const primaryEmail =
    customer?.addresses?.email?.find((e) => e.primary)?.address ||
    customer?.addresses?.email?.[0]?.address;
  const primaryPhone =
    customer?.addresses?.phone?.find((p) => p.primary)?.number ||
    customer?.addresses?.phone?.[0]?.number;

  const inline = variant === "inline";
  const compactContact = primaryPhone || primaryEmail;

  const compact = (
    <div className="min-w-0">
      <div className="truncate font-medium">
        <CustomerNameText customer={customer} fallback={fallback} />
      </div>
      {compactContact && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
          {primaryPhone ? (
            <PhoneIcon className="size-3 shrink-0" />
          ) : (
            <EnvelopeIcon className="size-3 shrink-0" />
          )}
          <span className="truncate">{compactContact}</span>
        </div>
      )}
    </div>
  );

  const content = inline ? (
    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
      <span className="flex items-center gap-2 font-medium text-foreground">
        <span className="flex size-7 items-center justify-center rounded-full bg-primary/10">
          <UserIcon className="size-4 text-primary" weight="duotone" />
        </span>
        <CustomerNameText
          customer={customer}
          fallback={fallback}
          markClassName="-ml-1.5"
        />
      </span>
      {primaryEmail && (
        <span className="flex min-w-0 items-center gap-1">
          <EnvelopeIcon className="size-3.5 shrink-0" />
          <span className="truncate">{primaryEmail}</span>
        </span>
      )}
      {primaryPhone && (
        <span className="flex items-center gap-1 tabular-nums">
          <PhoneIcon className="size-3.5 shrink-0" />
          {primaryPhone}
        </span>
      )}
    </div>
  ) : (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
        <UserIcon className="h-5 w-5 text-primary" weight="duotone" />
      </div>
      <div>
        <div className="font-medium">
          <CustomerNameText customer={customer} fallback={fallback} />
        </div>
        {(primaryEmail || primaryPhone) && (
          <div className="text-xs text-muted-foreground space-y-0.5">
            {primaryEmail && (
              <div className="flex items-center gap-1">
                <EnvelopeIcon className="h-3 w-3" />
                {primaryEmail}
              </div>
            )}
            {primaryPhone && (
              <div className="flex items-center gap-1">
                <PhoneIcon className="h-3 w-3" />
                {primaryPhone}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // No contact page on the client's file in the platform back-office: the
  // tile is drawn without a link there.
  const href = clientId && customerId ? links.contact(customerId) : null;
  if (clickable && href) {
    return (
      <Link to={href} className="hover:opacity-80 transition-opacity">
        {variant === "compact" ? compact : content}
      </Link>
    );
  }

  return variant === "compact" ? compact : content;
}
